// The values each deciding column can take, as the setup screen shows them:
// a master driver's distinct values with their record counts come from the
// server (`master-values`, read as the caller, capped so "more than the limit"
// is visible — plan D16); a form driver's from its edit-mask value list.
// Cached per source; `refresh` reads every source again and reports the values
// that are new since the last read (the prototype's "New value in the …
// master" banner).

import { useCallback, useEffect, useRef, useState } from 'react'
import type { UdaDriver, UdaMasterValues, UdaTransport } from '../types'
import { MASTER_VALUES_CAP } from '../limits'
import { columnByKey, type SetupForm } from './model'
import type { PickValue } from './picker'

export interface DriverDomain {
  values: PickValue[]
  total: number
  truncated: boolean
  groups: Array<{ label: string; values: string[] }>
  loading: boolean
  error: string | null
}

export interface NewValue { formNo: string; driverKey: string; code: string; label: string; count: number | null }

const EMPTY: DriverDomain = { values: [], total: 0, truncated: false, groups: [], loading: true, error: null }

export function sourceKey(formNo: string, d: UdaDriver): string {
  const f = d.from
  return f.kind === 'master' ? `${formNo}|m|${f.via.toLowerCase()}|${f.column.toLowerCase()}` : `${formNo}|f|${f.column.toLowerCase()}`
}

function formDomain(form: SetupForm | null, column: string): DriverDomain {
  const col = columnByKey(form, column)
  const vals = (col?.edit_mask?.values || []).map(v => [String(v.data ?? ''), String(v.display ?? v.data ?? ''), null] as PickValue)
    .filter(v => v[0])
  return {
    values: vals, total: vals.length, truncated: false, groups: [], loading: false,
    error: col ? null : `'${column}' is not a column of this form`,
  }
}

function masterDomain(r: UdaMasterValues): DriverDomain {
  return {
    values: (r.values || []).map(v => [String(v[0]), String(v[1] ?? v[0]), Number(v[2] ?? 0)] as PickValue),
    total: Number(r.total || 0), truncated: !!r.truncated, groups: r.groups || [], loading: false, error: null,
  }
}

function messageOf(err: unknown): string {
  const e = err as { message?: string; detail?: { message?: string }; response?: { data?: { detail?: { message?: string } | string } } }
  const d = e?.detail?.message || (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail
    : (e?.response?.data?.detail as { message?: string } | undefined)?.message)
  return d || e?.message || 'The values could not be read'
}

/** Read one master column's values (setup's "Decided by" counts too). */
export async function readMasterValues(transport: UdaTransport, objName: string, formNo: string,
  via: string, column: string, q?: string): Promise<DriverDomain> {
  try {
    const r = await transport.masterValues(objName, formNo, via, column, { q, limit: MASTER_VALUES_CAP })
    return masterDomain(r)
  } catch (err) {
    console.warn('[uda setup] master values failed', via, column, err)
    return { ...EMPTY, loading: false, error: messageOf(err) }
  }
}

export function useDomains(transport: UdaTransport, objName: string, forms: SetupForm[]) {
  const [domains, setDomains] = useState<Record<string, DriverDomain>>({})
  const domainsRef = useRef(domains)
  domainsRef.current = domains
  const [newValues, setNewValues] = useState<NewValue[]>([])
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const asked = useRef(new Set<string>())
  const formsRef = useRef(forms)
  formsRef.current = forms

  const fetchOne = useCallback(async (formNo: string, d: UdaDriver): Promise<DriverDomain> => {
    if (d.from.kind === 'form') {
      return formDomain(formsRef.current.find(f => f.formNo === formNo) || null, d.from.column)
    }
    return readMasterValues(transport, objName, formNo, d.from.via, d.from.column)
  }, [transport, objName])

  /** Make sure the domains of these drivers are loaded (once each). */
  const ensure = useCallback((formNo: string, drivers: UdaDriver[]) => {
    for (const d of drivers) {
      const k = sourceKey(formNo, d)
      if (asked.current.has(k)) continue
      asked.current.add(k)
      setDomains(s => ({ ...s, [k]: { ...EMPTY } }))
      void fetchOne(formNo, d).then(dom => setDomains(s => ({ ...s, [k]: dom })))
    }
  }, [fetchOne])

  const domainOf = useCallback((formNo: string, d: UdaDriver | null | undefined): DriverDomain => {
    if (!d) return { ...EMPTY, loading: false }
    return domains[sourceKey(formNo, d)] || EMPTY
  }, [domains])

  /** Read every driver's values again; values that are new are reported. */
  const refresh = useCallback(async (formNo: string, drivers: UdaDriver[]) => {
    setRefreshing(true)
    const found: NewValue[] = []
    try {
      const results = await Promise.all(drivers.map(async d => [d, await fetchOne(formNo, d)] as const))
      const s = domainsRef.current
      const next = { ...s }
      for (const [d, dom] of results) {
        const k = sourceKey(formNo, d)
        const before = new Set((s[k]?.values || []).map(v => v[0]))
        if (s[k] && !s[k].loading && !dom.error) {
          for (const v of dom.values) {
            if (!before.has(v[0])) found.push({ formNo, driverKey: d.key, code: v[0], label: v[1], count: v[2] ?? null })
          }
        }
        next[k] = dom
        asked.current.add(k)
      }
      domainsRef.current = next
      setDomains(next)
      setNewValues(prev => [...prev.filter(n => n.formNo !== formNo), ...found])
      setLastRefresh(new Date())
    } finally {
      setRefreshing(false)
    }
    return found
  }, [fetchOne])

  // A driver dropped from a form leaves its cache (harmless) — but forms
  // changing identity (a reloaded model) re-read form drivers next time.
  useEffect(() => {
    for (const k of [...asked.current]) if (k.includes('|f|')) asked.current.delete(k)
  }, [forms])

  return { domainOf, ensure, refresh, refreshing, newValues, lastRefresh, dismissNew: () => setNewValues([]) }
}

export type UdaDomains = ReturnType<typeof useDomains>
